"use client";

import { useEffect } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { useSessionContext } from "@/context";
import { routes } from "@/constants";
import { getPylonIdentityHash } from "@/api/client";

const PYLON_APP_ID = process.env.NEXT_PUBLIC_PYLON_APP_ID;

export const PylonChat = () => {
  const pathname = usePathname();
  const { user } = useSessionContext();

  // Fetch identity hash using TanStack Query
  const { data: identityHash, isFetched } = useQuery({
    queryKey: ["pylon-identity-hash", user?.email],
    queryFn: getPylonIdentityHash,
    enabled: Boolean(PYLON_APP_ID && user?.email),
    retry: false,
    staleTime: Infinity,
  });

  // Don't render Pylon on onboarding pages
  const isOnboarding = pathname.startsWith(routes.onboarding.index);

  // Initialize Pylon settings once identity hash is fetched
  const isReady = isFetched && Boolean(user);

  useEffect(() => {
    if (!PYLON_APP_ID || !user || !isFetched) return;

    window.pylon = {
      chat_settings: {
        app_id: PYLON_APP_ID,
        email: user.email,
        name:
          [user.firstname, user.lastname].filter(Boolean).join(" ") ||
          undefined,
        email_hash: identityHash?.hash || undefined,
      },
    };
  }, [user, isFetched, identityHash]);

  // Hide/show bubble when script was already loaded (e.g. navigating back, dev hot reload)
  useEffect(() => {
    if (!window.Pylon) return;

    if (isOnboarding) {
      window.Pylon("hideChatBubble");
    } else {
      window.Pylon("showChatBubble");
    }
  }, [isOnboarding]);

  if (!PYLON_APP_ID || !isReady || isOnboarding) {
    return null;
  }

  return (
    <Script id="pylon-chat" strategy="afterInteractive">
      {`(function(){var e=window;var t=document;var n=function(){n.e(arguments)};n.q=[];n.e=function(e){n.q.push(e)};e.Pylon=n;var r=function(){var e=t.createElement("script");e.setAttribute("type","text/javascript");e.setAttribute("async","true");e.setAttribute("src","https://widget.usepylon.com/widget/${PYLON_APP_ID}");var n=t.getElementsByTagName("script")[0];n.parentNode.insertBefore(e,n)};if(t.readyState==="complete"){r()}else if(e.addEventListener){e.addEventListener("load",r,false)}})();`}
    </Script>
  );
};
